// Configuration
const SCROLL_CONFIG = {
    MOBILE_BREAKPOINT: 1023,
    POSITION_THRESHOLD: 10,
    NEAR_TARGET_THRESHOLD_PX: 50,
    FOCUSABLE_ELEMENTS: ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']
}

const NAVIGATION_SELECTORS = {
    DESKTOP: '#desktopNavigation',
    MOBILE: '#mobileNavigation'
}

const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// State
const state = {
    initialized: false,
    isScrolling: false,
    scrollBehavior: prefersReducedMotion ? 'auto' : 'smooth',
    offset: 0,
    debounceDelay: 150,
    activeObservers: [],
    debouncedAutoAdjust: null,
    // Store bound handlers for removal
    handlers: {
        documentClick: null,
        userInteractAbort: null,
        hashChange: null,
        resize: null
    },
    autoScrollGeneration: 0,
    lastAutoScrollAt: 0,
    lastAutoScrollTargetId: null,
    lastUserScrollAt: 0,
}

// Utils
function createDebouncedFunction(originalFunction, delay) {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => originalFunction.apply(null, args), delay)
    }
}

function currentHashId() {
    return window.location.hash ? window.location.hash.slice(1) : null
}

function getNavigationHeight() {
    const isMobileView = window.innerWidth <= SCROLL_CONFIG.MOBILE_BREAKPOINT
    const navigationElement = document.querySelector(
        isMobileView ? NAVIGATION_SELECTORS.MOBILE : NAVIGATION_SELECTORS.DESKTOP
    )

    const navigationHeight = navigationElement?.offsetHeight || 0
    return navigationHeight + state.offset
}

function isViewportNearTarget() {
    const id = currentHashId()
    if (!id) return false
    const el = document.getElementById(id)
    if (!el) return false
    const topRelative = el.getBoundingClientRect().top - getNavigationHeight()
    return Math.abs(topRelative) < SCROLL_CONFIG.NEAR_TARGET_THRESHOLD_PX
}

// Scroller Logic
function scrollToPosition(targetY, behaviorOverride) {
    try {
        window.scrollTo({ top: targetY, behavior: behaviorOverride || state.scrollBehavior })
    } catch (e) {
        window.scrollTo(0, targetY)
    }
}

function computeAdjustedTargetY(element) {
    if (!element) return 0
    const elementTop = element.getBoundingClientRect().top
    const currentScrollY = window.pageYOffset
    const navigationHeight = getNavigationHeight()
    return Math.floor(elementTop + currentScrollY - navigationHeight)
}

function markAutoScroll(targetEl) {
    state.lastAutoScrollAt = Date.now()
    state.lastAutoScrollTargetId = (targetEl && targetEl.id) || currentHashId() || null
}

function animationLoop(targetElement, options, myGeneration, resolve) {
    let frameCount = 0
    let stableFrames = 0

    const performScrollAnimation = () => {
        if (state.autoScrollGeneration !== myGeneration) {
            resolve(false)
            return
        }
        frameCount++

        const targetScrollY = computeAdjustedTargetY(targetElement)
        const currentScrollY = window.pageYOffset
        const scrollDifference = Math.abs(currentScrollY - targetScrollY)

        if (scrollDifference > SCROLL_CONFIG.POSITION_THRESHOLD) {
            scrollToPosition(targetScrollY, options.behavior)
            stableFrames = 0
        } else {
            stableFrames++
        }

        const isMaxFramesReached = frameCount >= (options.maxFrames || 60)
        const isStableEnough = stableFrames >= (options.stableFramesRequired || 3)

        if (isMaxFramesReached || isStableEnough) {
            const isAlreadyFocusable = targetElement.hasAttribute('tabindex') ||
                SCROLL_CONFIG.FOCUSABLE_ELEMENTS.includes(targetElement.tagName)

            if (isAlreadyFocusable) {
                try { targetElement.focus({ preventScroll: true }) } catch (e) { }
            } else if (targetElement.id) {
                try {
                    targetElement.setAttribute('tabindex', '-1')
                    targetElement.focus({ preventScroll: true })
                    targetElement.removeAttribute('tabindex')
                } catch (e) { }
            }

            resolve(true)
        } else {
            requestAnimationFrame(performScrollAnimation)
        }
    }

    requestAnimationFrame(performScrollAnimation)
}

function scrollToElementWithAdjustment(targetElement, options = {}) {
    if (!targetElement) return Promise.resolve(false)

    state.isScrolling = true

    const myGeneration = state.autoScrollGeneration + 1
    state.autoScrollGeneration = myGeneration

    return new Promise((resolve) => {
        const myResolve = (result) => {
            state.isScrolling = false
            resolve(result)
        }

        markAutoScroll(targetElement)
        animationLoop(targetElement, options, myGeneration, myResolve)
    })
}

// Auto Adjust Logic
function shouldAutoAdjustOnDynamicChange() {
    if (state.isScrolling) return false
    const userInteractedAfterAuto = state.lastUserScrollAt > state.lastAutoScrollAt
    if (userInteractedAfterAuto) return false

    const id = currentHashId()
    if (!id || !document.getElementById(id)) return false

    const nearTarget = isViewportNearTarget()
    const sameTargetAsLastAuto = !!id && id === state.lastAutoScrollTargetId
    return sameTargetAsLastAuto || nearTarget
}

function autoAdjustIfSafe() {
    if (!window.location.hash) return
    if (shouldAutoAdjustOnDynamicChange()) {
        scrollToCurrentHash({ behavior: 'auto' })
    }
}

// Observers
function observeElement(selector) {
    const element = document.querySelector(selector)
    if (!element || typeof ResizeObserver === 'undefined') return
    const resizeObserver = new ResizeObserver(() => {
        state.debouncedAutoAdjust && state.debouncedAutoAdjust()
    })
    resizeObserver.observe(element)
    state.activeObservers.push(resizeObserver)
}

function observeIframes() {
    if (typeof document === 'undefined') return
    const observedIframes = new WeakSet()

    const attach = (iframe) => {
        if (observedIframes.has(iframe)) return
        observedIframes.add(iframe)
        iframe.addEventListener('load', () => {
            state.debouncedAutoAdjust && state.debouncedAutoAdjust()
        })
        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
                state.debouncedAutoAdjust && state.debouncedAutoAdjust()
            })
            try { resizeObserver.observe(iframe) } catch (e) { }
            state.activeObservers.push(resizeObserver)
        }
    }

    document.querySelectorAll('iframe').forEach(attach)

    if (typeof MutationObserver !== 'undefined') {
        const mutationObserver = new MutationObserver((records) => {
            for (const record of records) {
                record.addedNodes && record.addedNodes.forEach(node => {
                    if (node && node.nodeName === 'IFRAME') attach(node)
                    if (node && node.nodeType === 1) {
                        node.querySelectorAll && node.querySelectorAll('iframe').forEach(attach)
                    }
                })
            }
        })
        try { mutationObserver.observe(document.body, { childList: true, subtree: true }) } catch (e) { }
        state.activeObservers.push(mutationObserver)
    }
}

// Event Handlers
function onUserInteraction() {
    state.lastUserScrollAt = Date.now()
}

function onDocumentClick(event) {
    try {
        if (!event || event.defaultPrevented) return
        const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null
        if (!anchor) return

        const rawHref = anchor.getAttribute('href')
        if (!rawHref) return

        let url
        if (rawHref.startsWith('#')) {
            url = new URL(window.location.href)
            url.hash = rawHref
        } else {
            try { url = new URL(rawHref, window.location.href) } catch (e) { return }
        }

        if (!url.hash) return
        if (url.origin !== window.location.origin) return
        if (url.pathname !== window.location.pathname) return

        event.preventDefault()

        const id = url.hash.slice(1)
        const sameHash = currentHashId() === id
        const el = id ? document.getElementById(id) : null

        if (!sameHash) {
            try { window.history.pushState(null, '', `#${id}`) } catch (e) { }
        }

        if (el) {
            scrollToElementWithAdjustment(el)
        } else {
            scrollHashWhenAvailable()
        }
    } catch (e) { }
}

function onResizeEvent() {
    if (state.debouncedAutoAdjust) state.debouncedAutoAdjust()
}

function userInteractAbortHandler() {
    onUserInteraction()
    state.autoScrollGeneration++
}

function addEventListeners() {
    // Bind and store handlers
    state.handlers.documentClick = onDocumentClick
    state.handlers.userInteractAbort = userInteractAbortHandler
    state.handlers.resize = onResizeEvent
    state.handlers.hashChange = () => scrollToCurrentHash()

    window.addEventListener('hashchange', state.handlers.hashChange)
    window.addEventListener('resize', state.handlers.resize)
    window.addEventListener('wheel', state.handlers.userInteractAbort, { passive: true, capture: true })
    window.addEventListener('touchstart', state.handlers.userInteractAbort, { passive: true, capture: true })
    window.addEventListener('keydown', state.handlers.userInteractAbort, { capture: true })
    window.addEventListener('mousedown', state.handlers.userInteractAbort, { capture: true })
    document.addEventListener('click', state.handlers.documentClick)
}

function removeEventListeners() {
    const { handlers } = state
    
    if (handlers.hashChange) window.removeEventListener('hashchange', handlers.hashChange)
    if (handlers.resize) window.removeEventListener('resize', handlers.resize)
    
    if (handlers.userInteractAbort) {
        window.removeEventListener('wheel', handlers.userInteractAbort, { capture: true })
        window.removeEventListener('touchstart', handlers.userInteractAbort, { capture: true })
        window.removeEventListener('keydown', handlers.userInteractAbort, { capture: true })
        window.removeEventListener('mousedown', handlers.userInteractAbort, { capture: true })
    }

    if (handlers.documentClick) {
        document.removeEventListener('click', handlers.documentClick)
    }
}

// Public API Functions
function scrollToCurrentHash(options = {}) {
    if (!window.location.hash) return
    const elementId = window.location.hash.slice(1)
    if (!elementId) return
    const targetElement = document.getElementById(elementId)
    if (targetElement) {
        scrollToElementWithAdjustment(targetElement, options)
    }
}

async function scrollHashWhenAvailable(maxWaitMs = 1500) {
    const start = Date.now()
    if (!window.location.hash) return false
    const id = window.location.hash.slice(1)
    if (!id) return false

    return new Promise((resolve) => {
        const tryScroll = () => {
            const el = document.getElementById(id)
            if (el) {
                scrollToElementWithAdjustment(el).then(() => resolve(true))
                return
            }
            if (Date.now() - start >= maxWaitMs) {
                resolve(false)
                return
            }
            if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(tryScroll)
            } else {
                setTimeout(tryScroll, 16)
            }
        }
        tryScroll()
    })
}

function init(options = {}) {
    if (typeof window === 'undefined' || state.initialized) return

    state.initialized = true
    state.scrollBehavior = options.behavior || state.scrollBehavior
    state.offset = typeof options.offset === 'number' ? options.offset : state.offset
    state.debounceDelay = typeof options.debounceDelay === 'number' ? options.debounceDelay : state.debounceDelay

    try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual' } catch (e) { }

    state.debouncedAutoAdjust = createDebouncedFunction(autoAdjustIfSafe, state.debounceDelay)

    addEventListeners()

    observeElement(NAVIGATION_SELECTORS.DESKTOP)
    observeElement(NAVIGATION_SELECTORS.MOBILE)
    observeIframes()

    scrollToCurrentHash()
}

function destroy() {
    if (!state.initialized) return
    
    removeEventListeners()
    state.handlers = { documentClick: null, userInteractAbort: null, hashChange: null, resize: null }

    state.activeObservers.forEach(o => {
        try { o.disconnect && o.disconnect() } catch (e) { }
    })

    state.initialized = false
    state.activeObservers = []
    state.debouncedAutoAdjust = null
}

const anchorScrollManager = {
    init,
    destroy,
    scrollToCurrentHash,
    scrollHashWhenAvailable,
    scrollToElementWithAdjustment
}

export default anchorScrollManager
